import { Check, X, AlertTriangle, Phone, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Verdict = "yes" | "no" | "maybe";
const ROWS: {
  criterion: string;
  detail: string;
  skip: {
    v: Verdict;
    note: string;
  };
  diy: {
    v: Verdict;
    note: string;
  };
  generic: {
    v: Verdict;
    note: string;
  };
  gold: {
    v: Verdict;
    note: string;
  };
}[] = [{
  criterion: "Catches wear before it fails",
  detail: "Cycle count, tension drop, hairline cable fraying",
  skip: {
    v: "no",
    note: "No way to know until it snaps"
  },
  diy: {
    v: "maybe",
    note: "Eye-only, no measurement"
  },
  generic: {
    v: "maybe",
    note: "Depends on the tech's training"
  },
  gold: {
    v: "yes",
    note: "Measured & logged every visit"
  }
}, {
  criterion: "Written, photographed report",
  detail: "Pass / flag / fix-now on every checklist line",
  skip: {
    v: "no",
    note: "Nothing on paper, ever"
  },
  diy: {
    v: "no",
    note: "Just your memory"
  },
  generic: {
    v: "maybe",
    note: "Often verbal 'all good'"
  },
  gold: {
    v: "yes",
    note: "Stamped report, kept by you"
  }
}, {
  criterion: "Proper synthetic grease",
  detail: "Door-spec lube — not WD-40 (which attracts grit)",
  skip: {
    v: "no",
    note: "Nothing applied — wears faster"
  },
  diy: {
    v: "maybe",
    note: "Most use WD-40 (wrong product)"
  },
  generic: {
    v: "maybe",
    note: "Sometimes a generic spray"
  },
  gold: {
    v: "yes",
    note: "Door-spec synthetic only"
  }
}, {
  criterion: "Spring tension re-balanced",
  detail: "Adjust torsion to match the door's actual weight",
  skip: {
    v: "no",
    note: "Tension drops every year"
  },
  diy: {
    v: "no",
    note: "Dangerous DIY task — high tension"
  },
  generic: {
    v: "maybe",
    note: "Only if they have the bar"
  },
  gold: {
    v: "yes",
    note: "Every visit, every door"
  }
}, {
  criterion: "Safety reverse test (AS/NZS 4505)",
  detail: "50 mm block reversal — Australian household standard",
  skip: {
    v: "no",
    note: "Failing door = real risk"
  },
  diy: {
    v: "maybe",
    note: "Only if you know the standard"
  },
  generic: {
    v: "maybe",
    note: "Often skipped to save time"
  },
  gold: {
    v: "yes",
    note: "Standard part of every visit"
  }
}, {
  criterion: "Reminder set for next service",
  detail: "Auto SMS + booking call month before",
  skip: {
    v: "no",
    note: "Forgotten until it breaks"
  },
  diy: {
    v: "no",
    note: "You are the reminder system"
  },
  generic: {
    v: "no",
    note: "One-time job, one-time relationship"
  },
  gold: {
    v: "yes",
    note: "Auto reminder + 1-hr slot held"
  }
}, {
  criterion: "Same technician each visit",
  detail: "Knows your door's history & quirks",
  skip: {
    v: "no",
    note: "N/A — no visit happens"
  },
  diy: {
    v: "yes",
    note: "It's always you"
  },
  generic: {
    v: "no",
    note: "Random tech rotation"
  },
  gold: {
    v: "yes",
    note: "Continuity built in"
  }
}, {
  criterion: "1-Year Labour Warranty",
  detail: "On every part replaced during service",
  skip: {
    v: "no",
    note: "N/A — nothing was done"
  },
  diy: {
    v: "no",
    note: "You're on your own"
  },
  generic: {
    v: "maybe",
    note: "30-90 days if you're lucky"
  },
  gold: {
    v: "yes",
    note: "Full 12 months, no fine print"
  }
}];
function VerdictCell({
  verdict,
  note,
  highlight
}: {
  verdict: Verdict;
  note: string;
  highlight?: boolean;
}) {
  const Icon = verdict === "yes" ? Check : verdict === "no" ? X : AlertTriangle;
  const iconColor = verdict === "yes" ? highlight ? GOLD : "#7BD389" : verdict === "no" ? "#E5535B" : "#E5B547";
  const bg = verdict === "yes" ? highlight ? `${GOLD}22` : "#7BD38922" : verdict === "no" ? "#E5535B22" : "#E5B54722";
  const border = verdict === "yes" ? highlight ? `${GOLD}55` : "#7BD38955" : verdict === "no" ? "#E5535B55" : "#E5B54755";
  return <div className="flex items-start gap-2.5">
      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{
      width: 22,
      height: 22,
      backgroundColor: bg,
      border: `1px solid ${border}`
    }}>
        <Icon size={12} color={iconColor} strokeWidth={3} />
      </span>
      <span style={{
      fontFamily: FONT_BODY,
      fontSize: "12.5px",
      lineHeight: 1.5,
      color: highlight ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.65)",
      fontWeight: highlight ? 600 : 500
    }}>
        {note}
      </span>
    </div>;
}
export default function ServiceComparison() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 0%, ${NAVY_LIGHT}66 0%, ${NAVY} 60%), radial-gradient(circle at 50% 100%, ${GOLD}10 0%, transparent 50%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        07
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              07 · The Honest Comparison
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            Skip it · DIY · The other mob ·
            <br />
            <span style={{
            color: GOLD
          }}>
              or actually have it serviced
            </span>
            .
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Eight things that separate a real annual service from "she'll be
            right". Honest scorecard — no spin.
          </p>
        </div>

        {/* Comparison table */}
        <div className="rounded-2xl overflow-hidden" style={{
        backgroundColor: `${NAVY_DEEP}AA`,
        border: `1px solid rgba(255,255,255,0.08)`,
        boxShadow: "0 30px 80px rgba(0,0,0,0.4)"
      }}>
          {/* Header row */}
          <div className="grid grid-cols-[1.5fr_0.9fr_0.9fr_0.9fr_1.1fr] gap-0" style={{
          borderBottom: `1px solid rgba(255,255,255,0.1)`,
          backgroundColor: `${NAVY_LIGHT}33`
        }}>
            <div className="p-5 md:p-6">
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)"
            }}>
                Criterion
              </span>
            </div>
            <div className="p-5 md:p-6 text-center border-l" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 800,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "-0.01em",
              marginBottom: "2px"
            }}>
                Skip it
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Never service
              </div>
            </div>
            <div className="p-5 md:p-6 text-center border-l" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 800,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "-0.01em",
              marginBottom: "2px"
            }}>
                DIY
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Own tools
              </div>
            </div>
            <div className="p-5 md:p-6 text-center border-l" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "13px",
              fontWeight: 800,
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "-0.01em",
              marginBottom: "2px"
            }}>
                Generic Op
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.4)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Handyman
              </div>
            </div>
            <div className="p-5 md:p-6 text-center border-l relative" style={{
            borderColor: `${GOLD}55`,
            backgroundColor: `${GOLD}10`
          }}>
              {/* Gold ribbon */}
              <span className="absolute" style={{
              top: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
              height: "3px",
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`
            }} />
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                  <Sparkles size={12} color={GOLD} strokeWidth={2.5} />
                </span>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "13px",
                fontWeight: 900,
                color: GOLD,
                letterSpacing: "-0.01em"
              }}>
                  Gold
                </div>
              </div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: "10px",
              color: "rgba(255,255,255,0.55)",
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            }}>
                Your crew
              </div>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((row, i) => <div key={row.criterion} className="grid grid-cols-[1.5fr_0.9fr_0.9fr_0.9fr_1.1fr] gap-0" style={{
          borderBottom: i === ROWS.length - 1 ? "none" : `1px solid rgba(255,255,255,0.06)`
        }}>
              {/* Criterion */}
              <div className="p-5 md:p-6">
                <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "14.5px",
              color: "#ffffff",
              marginBottom: "4px",
              lineHeight: 1.25,
              letterSpacing: "-0.01em"
            }}>
                  {row.criterion}
                </h4>
                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "12px",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.5
            }}>
                  {row.detail}
                </p>
              </div>

              {/* Skip */}
              <div className="p-5 md:p-6 border-l" style={{
            borderColor: "rgba(255,255,255,0.06)"
          }}>
                <VerdictCell verdict={row.skip.v} note={row.skip.note} />
              </div>

              {/* DIY */}
              <div className="p-5 md:p-6 border-l" style={{
            borderColor: "rgba(255,255,255,0.06)"
          }}>
                <VerdictCell verdict={row.diy.v} note={row.diy.note} />
              </div>

              {/* Generic */}
              <div className="p-5 md:p-6 border-l" style={{
            borderColor: "rgba(255,255,255,0.06)"
          }}>
                <VerdictCell verdict={row.generic.v} note={row.generic.note} />
              </div>

              {/* Gold — highlighted */}
              <div className="p-5 md:p-6 border-l relative" style={{
            borderColor: `${GOLD}33`,
            backgroundColor: `${GOLD}08`
          }}>
                <VerdictCell verdict={row.gold.v} note={row.gold.note} highlight />
              </div>
            </div>)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          lineHeight: 1.6,
          color: "rgba(255,255,255,0.7)",
          maxWidth: "560px"
        }}>
            <span style={{
            color: GOLD,
            fontFamily: FONT_HEAD,
            fontWeight: 700
          }}>
              The honest truth:
            </span>{" "}
            DIY is fine if your door is brand new — but past year three, the
            value gap between a self-clean and a real service is huge.
          </p>

          <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "14px",
          boxShadow: `0 8px 24px ${GOLD}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={16} strokeWidth={2.5} />
            </span>
            Get a real service
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={14} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}